import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TeacherDashboard.css';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../../config/api.config';
import { toggleEnvironment } from '../../../../utils/apiClient';
import TopBar from '../../../../components/ui/TopBar/TopBar';

const TeacherDashboard = () => {
  const [stats, setStats] = useState({});
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch school details and update trial status
  const fetchSchoolDetails = async () => {
    try {
      const response = await axios.get(
        getApiUrl('/api/v1/stem@home/auth/school-details'),
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Update localStorage with correct account type
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          if (user && (user.accountType === 'trial') !== data.isTrial) {
            // Update the accountType to match what the server says
            user.accountType = data.isTrial ? 'trial' : 'paid';
            localStorage.setItem('user', JSON.stringify(user));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching school details:', err);
    }
  };

  useEffect(() => {
    // First, fetch school details to ensure trial status is updated
    fetchSchoolDetails();
    
    const fetchStats = async () => {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('No user data found');
        }
        const user = JSON.parse(userData);

        // Fetch teacher stats
        try {
          const classesResponse = await axios.get(
            getApiUrl(API_ENDPOINTS.TEACHER.CLASSES),
            { headers: getAuthHeaders() }
          );

          const questionsResponse = await axios.get(
            `/api/v1/stem@home/question/questions/teacher/${user.userId}`,
            {
              baseURL: toggleEnvironment(),
              headers: getAuthHeaders()
            }
          );

          const submissionsResponse = await axios.get(
            `/api/v1/stem@home/question/submissions/teacher/${user.userId}`,
            {
              baseURL: toggleEnvironment(),
              headers: getAuthHeaders()
            }
          );

          setStats({
            activeClasses: classesResponse.data.data?.length || 0,
            totalQuestions: questionsResponse.data.data?.length || 0,
            totalSubmissions: submissionsResponse.data.data?.length || 0
          });

          // Set recent submissions for teacher
          if (submissionsResponse.data.data) {
            const recentOnes = submissionsResponse.data.data
              .slice(0, 5)
              .map(submission => ({
                _id: submission._id,
                submittedAt: submission.submittedAt,
                student: { name: submission.student?.name || 'Unknown Student' },
                class: { name: submission.class?.name || 'Unknown Class' },
                totalScore: submission.totalScore
              }));
            setRecentSubmissions(recentOnes);
          }
        } catch (err) {
          console.error('Error fetching teacher stats:', err);
          setStats({
            activeClasses: 0,
            totalQuestions: 0,
            totalSubmissions: 0
          });
          setRecentSubmissions([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setStats({
          activeClasses: 0,
          totalQuestions: 0,
          totalSubmissions: 0
        });
        setRecentSubmissions([]);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <>
      <TopBar title="Dashboard" />
      <div className="dashboard-container" style={{ paddingTop: '64px' }}>
        <div className="page-content">
          <div className="dashboard-stats-container">

          <div className="stats-grid">
            <div 
              className="stat-card" 
              onClick={() => navigate('/classes')}
              style={{ cursor: 'pointer' }}
            >
              <h2>Active Classes</h2>
              <div className="number">{stats.activeClasses}</div>
            </div>
            <div 
              className="stat-card"
              onClick={() => navigate('/view-questions')}
              style={{ cursor: 'pointer' }}
            >
              <h2>Total Questions</h2>
              <div className="number">{stats.totalQuestions}</div>
            </div>
            <div 
              className="stat-card"
              onClick={() => navigate('/submissions')}
              style={{ cursor: 'pointer' }}
            >
              <h2>Total Submissions</h2>
              <div className="number">{stats.totalSubmissions}</div>
            </div>
          </div>

          <div className="recent-submissions">
            <div className="section-header">
              <h2>Recent Submissions</h2>
              <Link to="/submissions" className="view-all-link">View All</Link>
            </div>

            {recentSubmissions.length === 0 ? (
              <div key="no-submissions" className="no-submissions">No recent submissions</div>
            ) : (
              <div className="submissions-list">
                {recentSubmissions.map((submission) => (
                  <div 
                    key={submission._id} 
                    className="submission-card"
                    style={{
                      background: 'white',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: '1px solid #e9ecef'
                    }}
                    onClick={() => {
                      navigate('/submissions');
                      setTimeout(() => {
                        const event = new CustomEvent('viewSubmissionDetails', { 
                          detail: { submissionId: submission._id }
                        });
                        window.dispatchEvent(event);
                      }, 100);
                    }}
                  >
                    <div className="submission-header">
                      <div className="class-name">
                        Class: {submission.class?.name || 'Unknown Class'}
                      </div>
                      <div className="student-name">
                        {submission.student?.name || 'Unknown Student'}
                      </div>
                      <div className="submission-date">
                        {new Date(submission.submittedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })} ({new Date(submission.submittedAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })})
                      </div>
                    </div>
                    <div className="submission-score">
                      Score: {submission.totalScore || '0'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="action-buttons">
            {[
              {
                key: 'add-question',
                text: 'Add Question',
                onClick: () => navigate('/insert-question')
              },
              {
                key: 'view-questions',
                text: 'View Questions',
                onClick: () => navigate('/view-questions')
              },
              {
                key: 'create-class',
                text: 'Create Class',
                onClick: () => navigate('/create-class')
              },
              {
                key: 'upload-work',
                text: 'Upload Student Work',
                onClick: () => navigate('/upload-student-work')
              }
            ].map(button => (
              <button 
                key={button.key}
                onClick={button.onClick} 
                className="action-button"
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TeacherDashboard; 